import * as React from "react";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Eye, Pencil, Presentation } from "lucide-react";
import { Button } from "../../primitives/button";
import type { ActionDrawerStatus } from "../action-drawer/types";
import {
  ActionProposalSurface,
  type ActionProposalSurfaceTab,
} from "./ActionProposalSurface";
import { SlideOutlineEditor, type SlideObj } from "./SlideOutlineEditor";

/**
 * SlideDeckProposalSurface — the single work-area surface for a
 * presentation action proposal (``document_create`` OR ``document_edit`` of
 * a Marp deck). Editing a deck looks EXACTLY like creating one: the same
 * per-slide ``SlideOutlineEditor`` (Contenuto) plus a graphical thumbnail
 * preview (Anteprima), with the Confirm/Reject decision living INSIDE the
 * same surface — the proposal and the editor are ONE thing, not a banner
 * pointing at a separate viewer.
 *
 * Pure presentational — no store, no router, no fetcher. The container
 * (apps layer) owns:
 *   - the outline draft state (``slides`` / ``title`` + setters), sourced
 *     from the ``slide_list`` payload-schema via ``useSlideOutlineDraft``;
 *   - the graphical preview fetch (debounced ``POST /marp/preview``);
 *   - the Confirm transport (execute ``{ title, slides }``) + Reject.
 *
 * FIRST consumer of the generalised ``ActionProposalSurface`` shell: this
 * component now just supplies the slide-specific ``body`` (tabbed
 * SlideOutlineEditor + graphical preview) + the editable filename header;
 * the toolbar / decision-bar / status chrome lives in the shared shell so
 * every other action type (email, event, SF/FiC, …) reads as the same
 * family. Generalising to a new action type = a new ``body`` slot, the
 * chrome stays identical.
 *
 * Brand: cream surface, orange-accent active tab + primary Confirm CTA,
 * hairline borders, lucide 1.75–2 stroke. Matches ``MarpDeckEditor`` /
 * ``DraftActionBar`` so the surface reads as the same family.
 */

export type SlideDeckProposalTab = "content" | "preview";

export interface SlideDeckProposalSurfaceProps {
  /** Editable filename (BE ``title`` text field). */
  readonly title: string;
  readonly onTitleChange: (next: string) => void;
  /** Current slides array (controlled). */
  readonly slides: ReadonlyArray<SlideObj>;
  /** Allowed layout names (BE ``slide_list`` field ``options``). */
  readonly layoutOptions: ReadonlyArray<string>;
  readonly onSlidesChange: (next: SlideObj[]) => void;
  /**
   * Graphical preview state for the "Anteprima" tab. ``thumbnails`` is the
   * ordered list of slide PNG URLs; ``loading`` toggles the skeleton;
   * ``error`` surfaces a retry affordance. ``onRefresh`` re-fetches on
   * demand (the container debounces edits separately).
   */
  readonly preview: {
    readonly thumbnails: ReadonlyArray<string>;
    readonly loading: boolean;
    readonly error: string | null;
    readonly onRefresh: () => void;
  };
  /**
   * Optional custom node for the "Anteprima" tab. When provided it REPLACES the
   * marp thumbnail grid — used for OfficeCLI decks, where the preview renders
   * the REAL generated .pptx (``<PptxViewerInline>`` over the LibreOffice PDF)
   * instead of a marp re-render of the outline. When set, the marp
   * thumbnail fetch is skipped entirely.
   */
  readonly previewNode?: React.ReactNode;
  /**
   * When true (and ``previewNode`` is shown), a slim banner above the preview
   * tells the user it reflects the LAST GENERATED deck and that unsaved outline
   * edits apply only after Confirm/regenerate — removing the "I edited but the
   * preview didn't change" confusion. Only meaningful with ``previewNode``.
   */
  readonly previewStaleHint?: boolean;
  /** Active tab — controlled so the container can deep-link to a tab. */
  readonly activeTab: SlideDeckProposalTab;
  readonly onTabChange: (next: SlideDeckProposalTab) => void;
  /** Confirm CTA — caller awaits the BE execute/finalize mutation. */
  readonly onConfirm: () => void;
  /** Reject CTA — caller awaits the BE reject (or removes the proposal). */
  readonly onReject?: () => void;
  /** Spinner on Conferma + every interactive element disabled. */
  readonly busy?: boolean;
  /** Live lifecycle status forwarded to the shared shell's status badge. */
  readonly status?: ActionDrawerStatus;
  /** Eyebrow rationale shown next to the brand tag (truncated). */
  readonly rationale?: string;
  /** Confirm CTA label — defaults to "Conferma". */
  readonly confirmLabel?: string;
  readonly className?: string;
}

const TAB_DEFS: ReadonlyArray<
  Omit<ActionProposalSurfaceTab, "label"> & { readonly label: MessageDescriptor }
> = [
  { key: "content", label: msg`Contenuto`, Icon: Pencil },
  { key: "preview", label: msg`Anteprima`, Icon: Eye },
];

export const SlideDeckProposalSurface: React.FC<SlideDeckProposalSurfaceProps> = ({
  title,
  onTitleChange,
  slides,
  layoutOptions,
  onSlidesChange,
  preview,
  previewNode,
  previewStaleHint = false,
  activeTab,
  onTabChange,
  onConfirm,
  onReject,
  busy = false,
  status,
  rationale,
  confirmLabel,
  className,
}) => {
  const { i18n } = useLingui();
  const resolvedConfirmLabel = confirmLabel ?? i18n._(msg`Conferma`);
  // Refresh the graphical preview the first time the user opens the
  // "Anteprima" tab with no thumbnails yet — on-demand (not on every
  // keystroke), per the debounced-preview contract. Skipped when a custom
  // ``previewNode`` is supplied (OfficeCLI decks render the real .pptx; there
  // is no marp thumbnail fetch to trigger).
  const lastFetchedTab = React.useRef<SlideDeckProposalTab | null>(null);
  React.useEffect(() => {
    if (previewNode) return;
    if (
      activeTab === "preview" &&
      lastFetchedTab.current !== "preview" &&
      preview.thumbnails.length === 0 &&
      !preview.loading &&
      !preview.error
    ) {
      preview.onRefresh();
    }
    lastFetchedTab.current = activeTab;
  }, [activeTab, preview, previewNode]);

  // The slide-specific body — the ONLY part bespoke to a deck proposal.
  // Everything else (toolbar, decision bar, status, filename header) is the
  // shared ``ActionProposalSurface`` chrome.
  const body =
    activeTab === "content" ? (
      <div role="tabpanel" aria-label={i18n._(msg`Contenuto slide`)} className="px-4 py-4">
        <SlideOutlineEditor
          slides={slides}
          options={layoutOptions}
          onChange={onSlidesChange}
          disabled={busy}
        />
      </div>
    ) : (
      <div role="tabpanel" aria-label={i18n._(msg`Anteprima slide`)} className="px-4 py-4">
        {previewNode ? (
          <>
            {previewStaleHint ? (
              <div className="mb-2 rounded-md border border-hairline border-l-2 border-l-ando-orange/55 bg-card/60 px-3 py-1.5 text-[11.5px] text-ink-2">
                {i18n._(msg`L'anteprima mostra l'ultima versione generata. Premi «${resolvedConfirmLabel}» per applicare le tue modifiche.`)}
              </div>
            ) : null}
            {previewNode}
          </>
        ) : (
          <SlidePreviewGrid
            thumbnails={preview.thumbnails}
            slideCount={slides.length}
            loading={preview.loading}
            error={preview.error}
            onRefresh={preview.onRefresh}
          />
        )}
      </div>
    );

  return (
    <ActionProposalSurface
      className={className}
      tabs={TAB_DEFS.map((t) => ({ ...t, label: i18n._(t.label) }))}
      activeTab={activeTab}
      onTabChange={(next) => onTabChange(next as SlideDeckProposalTab)}
      tablistLabel={i18n._(msg`Modalità deck`)}
      icon={<Presentation className="h-3.5 w-3.5" strokeWidth={2} />}
      title={title}
      onTitleChange={onTitleChange}
      titlePlaceholder={i18n._(msg`Nome file`)}
      titleAriaLabel={i18n._(msg`Nome file`)}
      rationale={rationale}
      status={status}
      busy={busy}
      onConfirm={onConfirm}
      onReject={onReject}
      confirmLabel={resolvedConfirmLabel}
      confirmAriaLabel={i18n._(msg`Conferma e genera il deck`)}
      body={body}
    />
  );
};
SlideDeckProposalSurface.displayName = "SlideDeckProposalSurface";

interface SlidePreviewGridProps {
  readonly thumbnails: ReadonlyArray<string>;
  readonly slideCount: number;
  readonly loading: boolean;
  readonly error: string | null;
  readonly onRefresh: () => void;
}

/**
 * Responsive 16:9 thumbnail grid. While loading we render ``slideCount``
 * skeleton cards (so the grid doesn't collapse to a spinner); on error we
 * surface a retry button; on success we render the ordered PNGs.
 */
const SlidePreviewGrid: React.FC<SlidePreviewGridProps> = ({
  thumbnails,
  slideCount,
  loading,
  error,
  onRefresh,
}) => {
  const { i18n } = useLingui();
  if (error) {
    return (
      <div className="grid place-items-center px-6 py-12 text-center">
        <div className="space-y-3">
          <p className="text-[12.5px] text-ink-2">{error}</p>
          <Button type="button" variant="secondary" size="sm" onClick={onRefresh}>
            {i18n._(msg`Riprova anteprima`)}
          </Button>
        </div>
      </div>
    );
  }

  if (loading && thumbnails.length === 0) {
    const placeholders = Math.max(slideCount, 1);
    return (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: placeholders }).map((_, i) => (
          <div
            // Skeleton cards have no stable identity — index key is the
            // intended, lint-safe choice for a fixed-length placeholder list.
            key={`slide-skeleton-${i}`}
            className="aspect-video animate-pulse rounded-lg border border-hairline bg-cream-deep/50"
          />
        ))}
      </div>
    );
  }

  if (thumbnails.length === 0) {
    return (
      <div className="grid place-items-center px-6 py-12 text-center">
        <p className="text-[12.5px] text-ink-3 italic">
          {i18n._(msg`Nessuna anteprima disponibile. Modifica le slide e aggiorna.`)}
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {thumbnails.map((src, i) => (
        <figure
          key={src}
          className="relative overflow-hidden rounded-lg border border-hairline bg-white shadow-[0_2px_8px_-4px_rgba(0,0,0,0.12)]"
        >
          <span className="absolute top-1.5 left-1.5 z-10 inline-flex h-5 min-w-5 items-center justify-center rounded-md bg-ink-1/70 px-1.5 font-semibold text-[10px] text-white tabular-nums">
            {i + 1}
          </span>
          {/* Plain <img>: BE-rendered PNG, and @ando/ui must stay
              framework-agnostic (no Next image loader). Same pattern as
              ReviewRail / BrandMark / ConnectionPicker in this package. */}
          <img
            src={src}
            alt={i18n._(msg`Anteprima slide ${i + 1}`)}
            loading="lazy"
            className="aspect-video w-full object-contain"
          />
        </figure>
      ))}
    </div>
  );
};
SlidePreviewGrid.displayName = "SlideDeckProposalSurface.SlidePreviewGrid";
